import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BookOpenCheck, CircleSlash, RotateCcw, Search, X } from "lucide-react";
import BackButton from "components/ui/back-button";
import { Button } from "components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Input } from "components/ui/input";
import {
  DEMO_KNOWLEDGE_GAPS,
  DEMO_LINE_CONTEXT,
  practiceShortNameById,
} from "./mockData";
import {
  entriesForScope,
  hasUnsavedDemoEdits,
  KnowledgeScope,
  resetKnowledgeBank,
  summaryForScope,
  useKnowledgeBank,
} from "./knowledgeBankStore";
import { useCoverageSchedule } from "./coverageScheduleStore";
import { coverageScheduleLabel } from "./selectors";
import { KnowledgeEntry } from "./types";
import KnowledgeTierSection from "./KnowledgeTierSection";
import DemoDataBanner from "./DemoDataBanner";

/** Deep-link target used by the citations on a call detail page. */
export const KNOWLEDGE_BANK_PATH = "/dashboard/inbound-calls/knowledge-bank";

export function knowledgeEntryPath(entryId: string): string {
  return `${KNOWLEDGE_BANK_PATH}?entry=${entryId}`;
}

const matchesEntry = (entry: KnowledgeEntry, query: string): boolean => {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const corpus = `${entry.id} ${entry.question} ${entry.answer}`.toLowerCase();
  return terms.every((term) => corpus.includes(term));
};

/**
 * The readable, editable knowledge bank: every answer the assistant may give,
 * the description of each group, and the topics deliberately left uncovered.
 * Citations on a call link here, so any answer attributed to the bank can be
 * checked against the entry it came from.
 */
function KnowledgeBankPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const bank = useKnowledgeBank();
  const coverage = useCoverageSchedule();

  const highlightedId = searchParams.get("entry");

  useEffect(() => {
    if (highlightedId === null) {
      return;
    }
    // Optional call: jsdom has no scrollIntoView, and the highlight alone is
    // enough to locate the entry.
    document.getElementById(highlightedId)?.scrollIntoView?.({
      block: "center",
    });
  }, [highlightedId]);

  const scopes: Array<{
    scope: KnowledgeScope;
    slug: string;
    /** Who these answers are used for, e.g. "All locations". */
    title: string;
    /** Same thing worded to finish "Save this answer for …". */
    saveTargetLabel: string;
  }> = useMemo(
    () => [
      {
        scope: { tier: "ORG" },
        slug: "org",
        title: "All locations",
        saveTargetLabel: "all locations",
      },
      ...DEMO_LINE_CONTEXT.perPracticeSummaries.map((practiceSummary) => ({
        scope: {
          tier: "PRACTICE" as const,
          practiceId: practiceSummary.practiceId,
        },
        slug: practiceSummary.practiceId,
        title: `${practiceShortNameById(practiceSummary.practiceId)} only`,
        saveTargetLabel: practiceShortNameById(practiceSummary.practiceId),
      })),
    ],
    []
  );

  const isSearching = query.trim() !== "";
  const sections = scopes.map((section) => {
    const all = entriesForScope(bank, section.scope);
    return {
      ...section,
      summary: summaryForScope(bank, section.scope),
      totalCount: all.length,
      entries: isSearching
        ? all.filter((entry) => matchesEntry(entry, query))
        : all,
    };
  });

  const matchCount = sections.reduce(
    (sum, section) => sum + section.entries.length,
    0
  );
  const orgCount = sections[0].totalCount;
  const overlayCount = bank.entries.length - orgCount;
  const isEdited = hasUnsavedDemoEdits(bank);

  return (
    <div className="w-full bg-background">
      <div className="border-b-2 border-gray-100 px-4 py-2 sticky top-0 bg-white z-10 flex items-center justify-between gap-2">
        <BackButton onClick={() => navigate("/dashboard/inbound-calls")} />
        <DemoDataBanner />
      </div>

      <div className="p-4 space-y-4 max-w-4xl">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2">
              <BookOpenCheck className="h-5 w-5 text-gray-500" />
              Knowledge bank
            </h2>
            <p className="text-xs text-gray-500 mt-1" data-testid="kb-counts">
              {bank.entries.length} answers in total — {orgCount} used at all
              locations and {overlayCount} at one clinic only. If a caller asks
              anything that is not here, the assistant takes a message instead
              of guessing.
            </p>
          </div>
          {isEdited && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              data-testid="kb-reset"
              onClick={resetKnowledgeBank}
            >
              <RotateCcw className="h-3 w-3 mr-1" />
              Reset demo edits
            </Button>
          )}
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            data-testid="kb-search"
            aria-label="Search knowledge bank"
            placeholder="Search questions and answers…"
            className="h-8 pl-8 pr-8 text-xs"
          />
          {query !== "" && (
            <button
              type="button"
              aria-label="Clear search"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              onClick={() => setQuery("")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {isSearching && (
          <p
            className="text-xs text-muted-foreground"
            data-testid="kb-match-count"
          >
            {matchCount} of {bank.entries.length} answers match “{query.trim()}
            ”.
          </p>
        )}

        {sections.map((section) => (
          <KnowledgeTierSection
            key={section.slug}
            scope={section.scope}
            slug={section.slug}
            title={section.title}
            saveTargetLabel={section.saveTargetLabel}
            summary={section.summary}
            entries={section.entries}
            totalCount={section.totalCount}
            isSearching={isSearching}
            highlightedId={highlightedId}
          />
        ))}

        {/* The boundary of the bank matters as much as its contents: these are
            the questions callers asked that the assistant must refuse. */}
        <Card className="gap-3 py-4 border-amber-200 bg-amber-50">
          <CardHeader className="px-4">
            <CardTitle className="text-sm flex items-center gap-2">
              <CircleSlash className="h-4 w-4 text-amber-700" />
              Questions it cannot answer yet
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4" data-testid="kb-gaps">
            <p className="text-xs text-amber-900 mb-2">
              Callers asked about these and the assistant said it wasn’t sure,
              then took a message. Writing an answer above is what turns one of
              these into something it can say.
            </p>
            <ul className="list-disc list-inside text-sm text-gray-800 space-y-0.5">
              {DEMO_KNOWLEDGE_GAPS.map((gap) => (
                <li key={gap}>{gap}</li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <p className="text-xs text-gray-500">
          Edits here are a demo action: they live in this session and reset on
          reload. {coverageScheduleLabel(coverage)},{" "}
          {DEMO_LINE_CONTEXT.timezoneLabel}.  
        </p>
      </div>
    </div>
  );
}

export default KnowledgeBankPage;
